import React from "react";
import { DateTime } from "luxon";

import DatePicker from "app/components/DatePicker";
import { CalendarViewType } from "app/models/CalendarPreferences";
import ViewSelector from "./ViewSelector";

import styles from "./styles.module.scss";
import { useTranslation } from "react-i18next";
import { cls } from "app/utils";

type PropsDef = {
  date: DateTime;
  onNext: () => void;
  onPrev: () => void;
  onDateChange: (date: DateTime) => void;
  handleCalendarSettings?: () => void;
  /** Lets the Calendar route spotlight the gear while its settings popup is open. */
  settingsButtonRef?: React.RefObject<HTMLButtonElement>;
  initNewEvent: () => void;
  calendarView: string;
  dateFormat: (date: DateTime, calendarView: string) => string;
  currentView?: CalendarViewType;
  onViewChange?: (view: CalendarViewType) => void;
  showFacilityCalendar?: boolean;
  isFacilityCalendarView?: boolean;
  hideViewSelector?: boolean;
  hideDatePickerOnLarge?: boolean;
  // The "Resource Calendar" entry of the view menu. See ViewSelector.
  showResourceCalendar?: boolean;
  isResourceCalendarView?: boolean;
  onFacilityViewChange?: (resourceView: boolean) => void;
  facilityViewsOnly?: boolean;
};

function ToolbarDateDisplay({
  date,
  calendarView,
  dateFormat,
  onDateChange,
}: Pick<PropsDef, "date" | "calendarView" | "dateFormat" | "onDateChange">) {
  return (
    <DatePicker
      date={date}
      setDate={onDateChange}
      formatDate={(d) => dateFormat(d, calendarView)}
      data-testid="calendar-month-year"
    />
  );
}

export default function CalendarToolbar(props: PropsDef) {
  const {
    date,
    onNext,
    onPrev,
    onDateChange,
    handleCalendarSettings,
    settingsButtonRef,
    initNewEvent,
    calendarView,
    dateFormat,
    currentView,
    onViewChange,
    showFacilityCalendar,
    isFacilityCalendarView,
    hideViewSelector,
    hideDatePickerOnLarge,
    showResourceCalendar,
    isResourceCalendarView,
    onFacilityViewChange,
    facilityViewsOnly,
  } = props;
  const { t } = useTranslation();

  if (!date) {
    return null;
  }
  return (
    <div
      className={cls(styles.calendar_toolbar_container, {
        [styles.facility_toolbar]: isFacilityCalendarView,
      })}
    >
      <div className={cls(styles.toolbar_left, { [styles.hide_dropdown_on_large]: hideDatePickerOnLarge })}>
        <div className={styles.toolbar_date}>
          <ToolbarDateDisplay
            date={date}
            calendarView={calendarView}
            dateFormat={dateFormat}
            onDateChange={onDateChange}
          />
        </div>
      </div>

      <div className={styles.toolbar_right}>
        {currentView && onViewChange && (
          <>
            <button
              className={cls(
                styles.toolbar_nav_button,
                "btn",
                "btn-outline-secondary",
              )}
              style={{ transform: "rotate(90deg)" }}
              onClick={onPrev}
              data-testid="calendar-nav-prev"
            >
              <span className={styles.arrow_container}>
                <i className="ico ico-chevron" />
              </span>
            </button>
            <button
              onClick={() => onDateChange(DateTime.now())}
              type="button"
              className={cls(styles.button_today)}
            >
              {t("Today")}
            </button>
            <button
              className={cls(
                styles.toolbar_nav_button,
                "btn",
                "btn-outline-secondary",
              )}
              style={{ transform: "rotate(-90deg)" }}
              onClick={onNext}
              data-testid="calendar-nav-next"
            >
              <span className={styles.arrow_container}>
                <i className="ico ico-chevron" />
              </span>
            </button>
            {!hideViewSelector && (
              <ViewSelector
                currentView={currentView}
                onViewChange={onViewChange}
                showFacilityCalendar={showFacilityCalendar}
                isFacilityCalendarView={isFacilityCalendarView}
                showResourceCalendar={showResourceCalendar}
                isResourceCalendarView={isResourceCalendarView}
                onFacilityViewChange={onFacilityViewChange}
                facilityViewsOnly={facilityViewsOnly}
              />
            )}
            <div className={styles.divider} />
          </>
        )}

        <button
          onClick={initNewEvent}
          type="button"
          className={cls(styles.button_primary)}
          data-testid="calendar-add-button"
        >
          <span>+</span>
          <span className={styles.add_label}>{t("Add")}</span>
        </button>

        {handleCalendarSettings && (
          <button
            ref={settingsButtonRef}
            type="button"
            className={cls(
              styles.page_panel_settings_button,
              "btn",
              "btn-outline-secondary",
            )}
            onClick={handleCalendarSettings}
            data-testid="calendar-settings-button"
          >
            <i className={cls("ico ico-cog", styles.icon_settings)} />
          </button>
        )}
      </div>
    </div>
  );
}
